import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import FilterableSkuList, { SkuListColumn } from "components/FilterableSkuList";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import ProductHandouts from "./ProductHandouts";
import contextStore from "stores/contextStore";

const ProductDetails = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { registerPromise } = useTrashablePromises();
  const { id } = useParams<{ id: string }>();
  const { productEditUrl, productsUrl, productService } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const [deleteOpen, deleteOpenActions] = useBoolean(false);

  const { isLoading, isError, data } = useQuery(
    ["product", id],
    () => productService.getProductById(id!),
    {
      enabled: !!id,
    }
  );

  const product = data?.data;

  const getFirstNonEmpty = (...fields: (string | null | undefined)[]) =>
    fields.find((f) => !!f && f.trim() !== "");
  const productTitle =
    getFirstNonEmpty(product?.name, product?.value, product?.title) ||
    t({ id: "productDetails" });

  useTitle(productTitle);

  if (!id) return <Navigate to={productsUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="productLoadError" />
        </Alert>
      </Box>
    );
  }

  const handleDelete = async () => {
    await registerPromise(productService.disableProduct(product!.id!));
    queryClient.invalidateQueries("products");
    navigate(productsUrl());
  };

  const {
    name,
    value,
    description,
    title,
    urlPath,
    productType,
    crmId,
    ownerOrganizationName,
  } = product!;

  const rows = [
    {
      title: t({ id: "ownerOrganization" }),
      component: ownerOrganizationName && <span>{ownerOrganizationName}</span>,
    },

    {
      title: t({ id: "nameLabel" }),
      component: name && <span>{name}</span>,
    },

    {
      title: t({ id: "valueLabel" }),
      component: value && <span>{value}</span>,
    },

    {
      title: t({ id: "descriptionLabel" }),
      component: description && (
        <span dangerouslySetInnerHTML={{ __html: description }}></span>
      ),
    },

    {
      title: t({ id: "productTitleLabel" }),
      component: title && <span>{title}</span>,
    },

    {
      title: t({ id: "productUrlPathLabel" }),
      component: urlPath && <span>{urlPath}</span>,
    },

    {
      title: t({ id: "typeLabel" }),
      component: productType && <span>{productType.name}</span>,
    },
    {
      title: t({ id: "crmIdLabel" }) || "CRM ID",
      component: crmId && <span>{crmId}</span>,
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={productsUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>

      <Box my={3} display="flex" justifyContent="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={() => navigate(productEditUrl(id))}
        >
          <EditIcon />
        </Fab>
        <Box ml={4} />
        <Fab
          aria-label="add"
          color="secondary"
          onClick={deleteOpenActions.setTrue}
        >
          <DeleteIcon />
        </Fab>
      </Box>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="productDetails" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer>
            <Table>
              <TableBody>
                {rows.map(({ title, component }, i) => (
                  <TableRow key={i}>
                    <TableCell component="th" scope="row">
                      <b>{title}</b>
                    </TableCell>
                    <TableCell>{component}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="productRelatedSkusLabel" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FilterableSkuList
            organizationId={organizationId}
            preSelectedProductId={id}
            lazyTableTitle=""
            columns={[
              SkuListColumn.Sku,
              SkuListColumn.Title,
              SkuListColumn.SkuFormat,
              SkuListColumn.SkuAvailability,
              SkuListColumn.Status,
            ]}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="courseHandouts" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProductHandouts productId={id} isReadonly />
        </AccordionDetails>
      </Accordion>

      <DeleteEntityDialog
        dialogTitleKey="productDeleteTitle"
        contentKey="productDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
    </>
  );
};

export default ProductDetails;
